import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  TextField,
  Radio,
} from "@mui/material";
import {
  ConfirmDialogHost,
  openConfirmDialog,
} from "@/components/ConfirmDialog";
import { IconButton, Link, List, ListItem, MenuItem, Stack } from "@ui";
import { Close as CloseIcon, Add as AddIcon } from "@mui/icons-material";
import DoubleToggle from "./DoubleToggle";
import type { Company, Document } from "@/pages/private/dashboard/SharePage";
import { getCompanyDocs } from "@/shared/share/sharePageUtils";
import { declarationEndpoints } from "@/shared/api/endpoints";
import { httpGet, httpPost, httpPut } from "@/shared/api/http";
import {
  ApiResponse,
  CustomerDeclarationEntry,
  DeclarationInfoData,
  SharedDocResponse,
} from "@/shared/api/types";
import femaCapture from "@/shared/fema/capture";
import { useAuth } from "@/shared/state/store";
import { formatDate } from "@/shared/lib/utils/formatDate";
import { DocumentsPageService } from "@/shared/api/services/documentsPageService";
import { useAlert } from "@/app/providers/AlertProvider";
import { inferMimeType } from "@/shared/lib/utils/documents";
import { UI_MESSAGES } from "@/constants/ui";
import type { UploadedDocument } from "@/pages/private/dashboard/DocumentPage";
import DocumentsList from "./DocumentsList";
import SmartSuggestionsListUU from "./SmartSuggestionsListUU";
import { suggestionsHelpContent } from "@/shared/uu/suggestionsHelpContent";
import UuViewer from "./uu/uuEngine/uuViwer";
import { useUuFieldController } from "./uu/hooks/useUuFieldController";
import DocumentPreviewDialog from "./uu/uuUi/DocumentPreviewDialog";
import { ThemeColours } from "@/clients/acme/config/colours";
import InfoDialog from "./InfoDialog";
import JsonHelpDialog from "./JsonHelpDialog";
import { UU_SHOW_HIDE_INFO } from "@/shared/uuShowHide";
import { isAllowedEntrySource } from "@/constants/documents";

type SelectableCardProps = {
  title: string;
  description: string;
  selected: string;
  onSelect: (title: string) => void;
};

export function SelectableCard({
  title,
  description,
  selected,
  onSelect,
}: SelectableCardProps) {
  const isSelected = selected === title;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        px: 2,
        pt: 1.5,
        pb: 2,
        backgroundColor: isSelected
          ? title == "Reject"
            ? "#fff"
            : "#E8F9EF"
          : "#fff",
        border: `3px solid ${isSelected ? (title == "Reject" ? "#F42500" : "#22C55E") : "#ddd"}`,
        borderRadius: 2,
        mb: 2,
        cursor: "pointer",
      }}
      onClick={() => onSelect(title)}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Radio
          checked={isSelected}
          onChange={() => onSelect(title)}
          sx={{
            color: isSelected ? "#32D583" : "#000",
            "&.Mui-checked": {
              color: title == "Reject" ? "#EF4444" : "#32D583",
            },
            mr: 1,
            "& .MuiSvgIcon-root": {
              fontSize: 30,
            },
          }}
        />
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "20px", mb: 0 }}>
            {title}
          </Typography>
          {title != "Reject" && (
            <Typography sx={{ fontSize: "16px", color: "#333333" }}>
              {description}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function fieldToBoolean(field: string) {
  return field == "Y" ? true : field == "N" ? false : null;
}

function booleanToField(value: boolean | null) {
  return value === true ? "Y" : value === false ? "N" : null;
}

interface UUDeclarationProps {
  onClose: () => void;
  company: Company;
  setCompanies: React.Dispatch<React.SetStateAction<Company[]>>;
  handleDocumentToggle: (
    companyId: string,
    documentId: string,
    findId?: boolean,
  ) => void;
}

const UUDeclarationDialog: React.FC<UUDeclarationProps> = ({
  onClose,
  company,
  setCompanies,
  handleDocumentToggle,
}) => {
  const { showAlert } = useAlert();

  const { user } = useAuth();
  const uuController = useUuFieldController({ userId: user?.userId ?? null });

  const [helpDialogOpen, setHelpDialogOpen] = useState<{
    fieldName?: string;
  } | null>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [householdStatus, setHouseholdStatus] = useState<boolean | null>(null);
  const [changeInCircumstance, setChangeInCircumstance] = useState<
    boolean | null
  >(null);
  const [potentialChangeInCircumstances, setPotentialChangeInCircumstances] =
    useState<string>("");
  const [circumstanceNotes, setCircumstanceNotes] = useState<string>("");
  const [recievesPensionCredit, setRecievesPensionCredit] = useState<
    boolean | null
  >(null);
  const [recievesPensionCreditHousehold, setRecievesPensionCreditHousehold] =
    useState<boolean | null>(null);
  const [hasMedicalCondition, setHasMedicalCondition] = useState<
    boolean | null
  >(null);
  const [ieCustomerDeclarationInfo, setIeCustomerDeclarationInfo] =
    useState("");
  const [status, setStatus] = useState("");
  const [isNewData, setIsNewData] = useState<boolean | null>(true);
  const [raw, setRaw] = useState<CustomerDeclarationEntry>();

  const [documents, setDocuments] = useState<Document[]>([]);
  const [docsChange, setDocsChange] = useState<boolean>(false);

  const [suggestionsDocumentsCount, setSuggestionsDocumentsCount] =
    useState<number>(0);

  const [openInfoDialog, setOpenInfoDialog] = useState(false);

  const handleOpenPreview = async (doc: UploadedDocument) => {
    try {
      const documentId =
        doc.apiObject && "documentId" in doc.apiObject
          ? doc.apiObject.documentId
          : undefined;

      if (!documentId) {
        console.error("Document ID not found");
        return;
      }

      const data =
        await DocumentsPageService.downloadCustomerDocument(documentId);

      const blob =
        data instanceof Blob
          ? new Blob([data], { type: data.type || inferMimeType(doc.name) })
          : new Blob([data as unknown as BlobPart], {
              type: inferMimeType(doc.name),
            });

      const blobUrl = URL.createObjectURL(blob);

      console.log(inferMimeType(doc.name), blobUrl, data, data instanceof Blob);
    } catch (err) {
      console.error("Preview failed:", err);
    }
  };

  const circumstanceOptions: string[] = [
    "Bereavement",
    "Care Leaver aged under 25",
    "Drop of working hours",
    "Marital Split",
    "Redundancy",
    "Unable to work due to illness",
    "Other",
  ];

  const circumstanceSmartSuggestionsMap: Record<string, any> = {
    Bereavement: {
      fieldName: "deceasedOccupier",
      controller: {
        ...uuController.getFieldState("deceasedOccupier"),
        handleUploadDocument: async (file: File) => {
          await uuController
            .getFieldState("deceasedOccupier")
            ?.handleUploadDocument?.(file);
          setSuggestionsDocumentsCount((prev) => prev + 1);
        },
        handleDeleteDocument: async () => {
          await uuController
            .getFieldState("deceasedOccupier")
            ?.handleDeleteDocument?.();
          setSuggestionsDocumentsCount((prev) => prev - 1);
        },
      },
    },
    "Care Leaver aged under 25": null,
    "Drop of working hours": {
      fieldName: "changeInWorkingHours",
      controller: {
        ...uuController.getFieldState("changeInWorkingHours"),
        handleUploadDocument: async (file: File) => {
          await uuController
            .getFieldState("changeInWorkingHours")
            ?.handleUploadDocument?.(file);
          setSuggestionsDocumentsCount((prev) => prev + 1);
        },
        handleDeleteDocument: async () => {
          await uuController
            .getFieldState("changeInWorkingHours")
            ?.handleDeleteDocument?.();
          setSuggestionsDocumentsCount((prev) => prev - 1);
        },
      },
    },
    "Marital Split": {
      fieldName: "maritalSplit",
      controller: {
        ...uuController.getFieldState("maritalSplit"),
        handleUploadDocument: async (file: File) => {
          await uuController
            .getFieldState("maritalSplit")
            ?.handleUploadDocument?.(file);
          setSuggestionsDocumentsCount((prev) => prev + 1);
        },
        handleDeleteDocument: async () => {
          await uuController
            .getFieldState("maritalSplit")
            ?.handleDeleteDocument?.();
          setSuggestionsDocumentsCount((prev) => prev - 1);
        },
      },
    },
    Redundancy: {
      fieldName: "redundancy",
      controller: {
        ...uuController.getFieldState("redundancy"),
        handleUploadDocument: async (file: File) => {
          await uuController
            .getFieldState("redundancy")
            ?.handleUploadDocument?.(file);
          setSuggestionsDocumentsCount((prev) => prev + 1);
        },
        handleDeleteDocument: async () => {
          await uuController
            .getFieldState("redundancy")
            ?.handleDeleteDocument?.();
          setSuggestionsDocumentsCount((prev) => prev - 1);
        },
      },
    },
    "Unable to work due to illness": {
      fieldName: "dropInIncomeDueToIllness",
      controller: {
        ...uuController.getFieldState("dropInIncomeDueToIllness"),
        handleUploadDocument: async (file: File) => {
          await uuController
            .getFieldState("dropInIncomeDueToIllness")
            ?.handleUploadDocument?.(file);
          setSuggestionsDocumentsCount((prev) => prev + 1);
        },
        handleDeleteDocument: async () => {
          await uuController
            .getFieldState("dropInIncomeDueToIllness")
            ?.handleDeleteDocument?.();
          setSuggestionsDocumentsCount((prev) => prev - 1);
        },
      },
    },
    Other: null,
  };

  const radioCards = [
    {
      title: "Yes, I accept",
      description: "I give explicit consent for United Utilities.",
    },
    {
      title: "Reject",
      description:
        "I do not give consent for IE Hub to add or share my health-related response.",
    },
  ];

  // GET declaration data
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getDeclarationData() {
      try {
        const response = await httpGet<ApiResponse>(
          declarationEndpoints.getDeclaration(customerData.customerId),
        );

        const data: any = response?.data;

        const res = data.find((entry: CustomerDeclarationEntry) => {
          return String(company.id) === String(entry.sharingId);
        });

        console.log(res);

        const {
          isPrimaryResidence,
          hasNegativeCircumstances,
          negativeCircumstancesDescription,
          hasPensionCreditInHousehold,
          allAdultsReceivingPension,
          hasMedicalConditionAffectingWaterUsage,
          potentialChangeInCircumstances,
          declarationStatus,
        } = res;

        setHouseholdStatus(fieldToBoolean(isPrimaryResidence));
        setChangeInCircumstance(fieldToBoolean(hasNegativeCircumstances));
        setPotentialChangeInCircumstances(potentialChangeInCircumstances);
        setCircumstanceNotes(negativeCircumstancesDescription);
        setRecievesPensionCredit(fieldToBoolean(hasPensionCreditInHousehold));
        setRecievesPensionCreditHousehold(
          fieldToBoolean(allAdultsReceivingPension),
        );
        setHasMedicalCondition(
          fieldToBoolean(hasMedicalConditionAffectingWaterUsage),
        );
        
        if (company.declarationStatus == 'not-completed') {
          setStatus("");
        } else {
          setStatus(declarationStatus == "Accept" ? "Yes, I accept" : "");
        }

        setRaw(res);

        setIsNewData(false);
      } catch (error) {
        femaCapture("fetching_declaration_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getDeclarationData();
  }, []);

  // GET declaration info data
  useEffect(() => {
    async function getDeclarationInfoData() {
      try {
        const response = await httpGet<ApiResponse>(
          declarationEndpoints.fetchDeclarationInfo(),
        );

        const data: any = response?.data;

        const res = data.find((item: DeclarationInfoData) => {
          return item.lender == "United Utilities";
        });

        setIeCustomerDeclarationInfo(res.infoText);
      } catch (error) {
        femaCapture("fetching_declaration_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getDeclarationInfoData();
  }, []);

  const handleCheckDocument = async (id: string) => {
    setDocuments((prev) => {
      for (const doc of prev) {
        if (doc.id == String(id)) {
          console.log("check", doc.documentId, company.id, doc);
          handleDocumentToggle(company.id, String(doc.documentId), true);
        }
      }

      return prev;
    });
  };

  const handleDeleteDocument = async (id: string) => {
    try {
      const yes = await openConfirmDialog({
        title: "Delete Document",
        message: "Are you sure you want to delete this?",
        confirmText: "Delete",
        cancelText: "Cancel",
      });

      if (!yes) {
        return;
      }

      const customerData = JSON.parse(
        localStorage.getItem("customerData") as string,
      );
      await DocumentsPageService.deleteCustomerDocument(
        customerData.customerId,
        id,
      );
      setDocuments((prev) => prev.filter((doc) => doc.id !== id));
    } catch (e) {
      console.log("An error occurred when deleting document with id:", id);
    }
  };

  const handleDocumentInput = async (id: string, value: string) => {
    try {
      setDocuments((prev) =>
        prev.map((doc) => {
          if (doc.id == id) {
            doc.description = value;
          }

          return doc;
        }),
      );

      setDocsChange(true);
    } catch (e) {
      console.log("An error occurred when editing document with id:", id);
    }
  };

  const handlePotentialCircumstanceChange = async (e) => {
    const nextValue = e.target.value;
    const oldVal = potentialChangeInCircumstances;

    const controller = circumstanceSmartSuggestionsMap[oldVal];
    const fieldName = controller?.fieldName;
    const uuFieldState = uuController.getFieldState(fieldName);

    console.log(
      "About to change to:",
      nextValue,
      "from:",
      fieldName,
      uuFieldState,
    );

    if (uuFieldState?.uploadedDocument) {
      const yes = await openConfirmDialog({
        title: "Change Selection",
        message:
          "Are you sure you want to change your selection? This will delete the corresponding document proof.",
        confirmText: "Confirm",
        cancelText: "Cancel",
      });

      if (!yes) {
        setPotentialChangeInCircumstances(oldVal);
        return;
      } else {
        uuFieldState && uuFieldState?.handleDeleteDocument?.();
        setPotentialChangeInCircumstances(nextValue);
      }
    } else {
      setPotentialChangeInCircumstances(nextValue);
    }
  };

  const totalDocumentsLength = useMemo(() => {
    localStorage.setItem(
      "totalDocumentsLength",
      String(documents.length + suggestionsDocumentsCount),
    );

    console.log(
      "totalDocumentsLength",
      documents.length + suggestionsDocumentsCount,
    );

    return documents.length + suggestionsDocumentsCount;
  }, [documents, suggestionsDocumentsCount]);

  const handleFileSelect = useCallback(async () => {
    if (isUploading) {
      return;
    }

    if (totalDocumentsLength >= 10) {
      showAlert({
        severity: "warning",
        message:
          "Looks like you’ve hit the max! You can upload up to 10 documents.",
        autoHide: true,
        duration: 5000,
      });
      return;
    }

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    // Trigger file input click
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.onchange = async (e: Event) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (totalDocumentsLength + files.length > 10) {
        showAlert({
          severity: "warning",
          message:
            "Looks like you’ve hit the max! You can upload up to 10 documents.",
          autoHide: true,
          duration: 5000,
        });
        return;
      }

      const allowedExtensions = ["pdf", "png", "jpg", "jpeg"];

      for (const file of files) {
        const ext = file.name.split(".").pop()?.toLowerCase();

        if (!ext || !allowedExtensions.includes(ext)) {
          showAlert({
            severity: "error",
            message:
              "Invalid file format. You can only upload PDF, PNG, JPG, or JPEG files.",
            duration: 3000,
          });
          return;
        }

        if (file.size > 5 * 1024 * 1024) {
          showAlert({
            severity: "error",
            message:
              "The file is too large. Please select a file smaller than 5MB.",
            duration: 3000,
          });
          return;
        }
      }

      setIsUploading(true);
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);

        const response = await DocumentsPageService.uploadCustomerDocument(
          customerData.customerId,
          formData,
          "United Utilities",
          "",
        );

        const {
          id,
          documentSize,
          fileName,
          creationDate,
          documentName,
          documentId,
        } = response;

        console.log(response);

        setDocuments((prev) => [
          ...prev,
          {
            id: String(id),
            name: fileName,
            documentId,
            size: documentSize,
            description: documentName,
            uploadedDate: formatDate(creationDate),
            type: "wage-slip",
            isNewlyUploaded: true,
            apiObject: response,
          },
        ]);
      }

      showAlert({
        severity: "success",
        message: `Successfully uploaded ${files.length} document${files.length > 1 ? "s" : ""}`,
        autoHide: true,
        duration: 5000,
      });
      setIsUploading(false);
    };

    input.click();
  }, [documents, isUploading, totalDocumentsLength, showAlert]);

  const fetchDocuments = async (): Promise<Document[]> => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );
    try {
      const data = await DocumentsPageService.getCustomerDocuments(
        customerData.customerId,
      );

      const documentsArray = Array.isArray(data)
        ? data
        : (data as ApiResponse<any[]>).data || [];

      return documentsArray.map((doc: any) => ({
        id: String(doc.id),
        name: doc.fileName,
        documentId: doc.documentId,
        size: doc.documentSize,
        description: doc.documentName,
        uploadedDate: formatDate(doc.creationDate),
        type: "wage-slip",
        isNewlyUploaded: false,
        apiObject: doc,
      }));
    } catch (error) {
      console.error("Failed to fetch companies", error);
      return [];
    }
  };

  // Fetch documents
  useEffect(() => {
    fetchDocuments().then((documentsArray) => {
      setSuggestionsDocumentsCount(
        documentsArray.filter((c) => {
          const apiObj = (c as any).apiObject;
          return (
            apiObj &&
            "entrySource" in apiObj &&
            apiObj.entrySource &&
            !isAllowedEntrySource(apiObj.entrySource)
          );
        }).length,
      );

      setDocuments(
        documentsArray
          .filter((c) => {
            const apiObj = (c as any).apiObject;
            const entrySource =
              apiObj && "entrySource" in apiObj ? apiObj.entrySource : null;
            return isAllowedEntrySource(entrySource);
          })
          .map((doc) => {
            const findDocFromSharedDocs = company.documents.find(
              (d) => d.raw.documentId == doc.documentId,
            );

            if (findDocFromSharedDocs) {
              doc.secondId = findDocFromSharedDocs.id;
            }

            return doc;
          }),
      );
    });
  }, []);

  function isBoolean(val: any) {
    return val === false || val === true;
  }

  async function handleSave() {
    const circumstancesValidation =
      changeInCircumstance === true
        ? !potentialChangeInCircumstances ||
          !circumstanceNotes ||
          circumstanceNotes.length <= 0
          ? false
          : true
        : true;

    const declarationValidation =
      status == "Yes, I accept" &&
      circumstancesValidation &&
      isBoolean(householdStatus) &&
      isBoolean(changeInCircumstance) &&
      isBoolean(hasMedicalCondition) &&
      isBoolean(recievesPensionCredit) &&
      isBoolean(recievesPensionCreditHousehold);

    if (status != "Reject" && !declarationValidation) {
      showAlert({
        severity: "warning",
        message: "Please fill out all required fields.",
        autoHide: true,
        duration: 2500,
      });
      return;
    }

    if (
      changeInCircumstance === true &&
      circumstanceNotes &&
      circumstanceNotes.length < 5
    ) {
      showAlert({
        severity: "warning",
        message:
          "'Negative Circumstances Description' must be a minimum of 5 characters.",
        autoHide: true,
        duration: 2500,
      });
      return;
    }

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    let newCompanyDocs: Document[] = [];

    if (docsChange) {
      for (const doc of documents) {
        const payload = {
          id: doc.id,
          customerId: customerData.customerId,
          documentId: Number(doc.documentId),
          documentName: doc.description as string,
          documentSize: doc.size,
          fileName: doc.name,
        };

        const docResp: SharedDocResponse =
          await DocumentsPageService.updateCustomerDocument(payload);

        // redundant code, remove later
        newCompanyDocs.push({
          id: doc.id,
          name: doc.description as string,
          size: doc.size,
          type: "wage-slip",
          uploadedDate: formatDate(docResp.lastUpdateDate),
          raw: docResp,
        });
      }
    }

    // create new entry
    if (isNewData) {
      try {
        const payload = {
          isPrimaryResidence: booleanToField(householdStatus),
          hasNegativeCircumstances: booleanToField(changeInCircumstance),
          potentialChangeInCircumstances: potentialChangeInCircumstances,
          negativeCircumstancesDescription: circumstanceNotes,
          hasPensionCreditInHousehold: booleanToField(recievesPensionCredit),
          allAdultsReceivingPension: booleanToField(
            recievesPensionCreditHousehold,
          ),
          hasMedicalConditionAffectingWaterUsage:
            booleanToField(hasMedicalCondition),
          sharingId: company.id,
          customerId: customerData.customerId,
          accountNumber: company.reference,
          declarationStatus: status == "Yes, I accept" ? "Accept" : "Reject",
        };

        await httpPost<CustomerDeclarationEntry>(
          declarationEndpoints.createDeclaration(),
          payload,
        );
      } catch (e) {
        showAlert({
          severity: "error",
          message: "Oops! Something went wrong",
          autoHide: true,
          duration: 2500,
        });

        femaCapture("create_declaration_data_error", e, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        return;
      }
    } else {
      try {
        // update existing entry
        const payload = {
          ...raw,
          isPrimaryResidence: booleanToField(householdStatus),
          hasNegativeCircumstances: booleanToField(changeInCircumstance),
          potentialChangeInCircumstances: potentialChangeInCircumstances,
          negativeCircumstancesDescription: circumstanceNotes,
          hasPensionCreditInHousehold: booleanToField(recievesPensionCredit),
          allAdultsReceivingPension: booleanToField(
            recievesPensionCreditHousehold,
          ),
          hasMedicalConditionAffectingWaterUsage:
            booleanToField(hasMedicalCondition),
          declarationStatus: status == "Yes, I accept" ? "Accept" : "Reject",
        };

        await httpPut<CustomerDeclarationEntry>(
          declarationEndpoints.createDeclaration(),
          payload,
        );
      } catch (e) {
        showAlert({
          severity: "error",
          message: "Oops! Something went wrong",
          autoHide: true,
          duration: 2500,
        });

        femaCapture("update_declaration_data_error", e, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        return;
      }
    }

    const lenderName = company.raw?.lenderName || "";
    const accountNumber = company.raw?.accountNumber || "";
    const companyDocsResponse = await getCompanyDocs(
      company.id,
      lenderName,
      accountNumber,
    );

    const companyDocsArray = Array.isArray(companyDocsResponse)
      ? companyDocsResponse
      : (companyDocsResponse as ApiResponse<any[]>).data || [];

    const newDocs: Document[] = companyDocsArray.map(
      (doc: SharedDocResponse) => {
        return {
          id: String(doc.id),
          name: doc.documentName || doc.fileName,
          size: doc.documentSize,
          type: "wage-slip",
          uploadedDate: formatDate(doc.lastUpdateDate),
          raw: doc,
        };
      },
    );

    let selectedDocuments: string[] = [];

    for (const doc of newDocs) {
      if (doc?.raw?.isShared == "Y") {
        selectedDocuments.push(doc.id);
      }
    }

    setCompanies((prev) => {
      prev.map(async (c) => {
        if (c.id === company.id) {
          c.declarationStatus =
            status == "Yes, I accept" ? "accepted" : "rejected";

          if (c.declarationStatus == "rejected" && c.raw) {
            c.raw.shareNow = "N";
          } else if (c.declarationStatus == "accepted" && c.raw) {
            c.raw.shareNow = "Y";
          }

          c.documents = newDocs;
          c.selectedDocuments = selectedDocuments;
        }

        return c;
      });

      return prev;
    });

    onClose();
  }

  async function handleClose() {
    const lenderName = company.raw?.lenderName || "";
    const accountNumber = company.raw?.accountNumber || "";
    const companyDocsResponse = await getCompanyDocs(
      company.id,
      lenderName,
      accountNumber,
    );

    const companyDocsArray = Array.isArray(companyDocsResponse)
      ? companyDocsResponse
      : (companyDocsResponse as ApiResponse<any[]>).data || [];

    const newDocs: Document[] = companyDocsArray.map(
      (doc: SharedDocResponse) => {
        return {
          id: String(doc.id),
          name: doc.documentName || doc.fileName,
          size: doc.documentSize,
          type: "wage-slip",
          uploadedDate: formatDate(doc.lastUpdateDate),
          raw: doc,
        };
      },
    );

    let selectedDocuments: string[] = [];

    for (const doc of newDocs) {
      if (doc?.raw?.isShared == "Y") {
        selectedDocuments.push(doc.id);
      }
    }

    setCompanies((prev) => {
      prev.map(async (c) => {
        if (c.id === company.id) {
          c.documents = newDocs;
          c.selectedDocuments = selectedDocuments;
        }

        return c;
      });

      return prev;
    });

    onClose();
  }

  const renderInfoDialog = () => {
    return (
      <InfoDialog
        open={openInfoDialog}
        onClose={() => {
          setOpenInfoDialog(false);
        }}
        company={{ name: "United Utilities Info" } as Company}
        infoText={ieCustomerDeclarationInfo}
        maxWidth="md"
      />
    );
  };

  const uuFieldState = uuController.getFieldState("medicalEvidence");
  const asyncDateHandler = uuFieldState?.handleDateChange;

  const handleDateChangeWrapper = asyncDateHandler
    ? (month: string, year: string): void => {
        void asyncDateHandler(month, year);
      }
    : undefined;

  const handleDocumentUpload = async (file: File) => {
    if (uuFieldState?.handleUploadDocument) {
      await uuFieldState.handleUploadDocument(file);
    }

    setSuggestionsDocumentsCount((prev) => prev + 1);
  };

  const handleDeleteDocumentUU = async () => {
    if (uuFieldState?.handleDeleteDocument) {
      await uuFieldState.handleDeleteDocument();
    }

    setSuggestionsDocumentsCount((prev) => prev - 1);
  };

  return (
    <>
      {renderInfoDialog()}
      <ConfirmDialogHost />
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Dialog Title */}
        <DialogTitle
          sx={{
            borderBottom: "1px solid #e0e0e0",
            px: 4,
            justifyContent: "space-between",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            United Utilities Declaration
          </Typography>

          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme: any) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Dialog Content */}
        <DialogContent sx={{ pt: 3, px: 4, flexGrow: 1, overflowY: "auto" }}>
          <Box sx={{ pt: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Completing these steps helps United Utilities provide you with the
              support you need. Eligibility for support schemes may require
              further review.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              pt: 1.5,
              pb: 2,
              backgroundColor: "#E8EFEE",
              border: "1px solid #165F54",
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Stack>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 0 }}>
                Confirming household status
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 400, mb: 1.5 }}>
                Can you please confirm if the premises you are living in, is
                your only home? 
                <Box component="span" sx={{ color: "red", ml: 0.5 }}>
                  *
                </Box>
              </Typography>
              <DoubleToggle
                value={householdStatus}
                onChange={setHouseholdStatus}
                selectedColor="#165F54"
                textColor="#FFFFFF"
              />
            </Stack>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              pt: 1.5,
              pb: 2,
              backgroundColor: "#E8EFEE",
              border: "1px solid #165F54",
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Stack sx={{ width: "100%" }}>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 0 }}>
                Confirming household circumstances
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 400, mb: 1.5 }}>
                Have you had a change in circumstance within the last 6 months
                that has reduced your household income?{" "}
                <Box component="span" sx={{ color: "red", ml: 0.5 }}>
                  *
                </Box>
              </Typography>
              <DoubleToggle
                value={changeInCircumstance}
                onChange={setChangeInCircumstance}
                selectedColor="#165F54"
                textColor="#FFFFFF"
                sx={{ mb: 2 }}
              />

              {changeInCircumstance && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.8 }}>
                    Potential Change in Circumstances{" "}
                    <Box component="span" sx={{ color: "red", ml: 0.5 }}>
                      *
                    </Box>
                  </Typography>
                  <Select
                    labelId="change-in-circumstances-label"
                    value={potentialChangeInCircumstances}
                    sx={{ backgroundColor: "#fff", mb: 1.5 }}
                    onChange={(e) => handlePotentialCircumstanceChange(e)}
                  >
                    {circumstanceOptions.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.8 }}>
                    Describe Negative Circumstances{" "}
                    <Box component="span" sx={{ color: "red", ml: 0.5 }}>
                      *
                    </Box>
                  </Typography>
                  <TextField
                    value={circumstanceNotes}
                    onKeyDown={(e) => {
                      if (
                        circumstanceNotes.length >= 200 &&
                        e.key.length === 1 &&
                        !e.ctrlKey &&
                        !e.metaKey
                      ) {
                        showAlert({
                          severity: "warning",
                          message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                        });
                      }
                    }}
                    onPaste={(e) => {
                      const pastedText = e.clipboardData.getData("text");
                      const selectionStart = (e.target as any).selectionStart || 0;
                      const selectionEnd = (e.target as any).selectionEnd || 0;
                      const currentLength = circumstanceNotes.length;
                      if (
                        currentLength - (selectionEnd - selectionStart) + pastedText.length >
                        200
                      ) {
                        showAlert({
                          severity: "warning",
                          message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                        });
                      }
                    }}
                    onChange={(e) => setCircumstanceNotes(e.target.value)}
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Describe the Negative Circumstances"
                    variant="outlined" // or "filled" depending on style
                    InputProps={{
                      style: {
                        backgroundColor: "white", // makes background white
                      },
                    }}
                    inputProps={{ maxLength: 200 }}
                  />

                  {!UU_SHOW_HIDE_INFO.uuShowHideInfo && (
                    <Box>
                      {circumstanceSmartSuggestionsMap[
                        potentialChangeInCircumstances
                      ]?.fieldName && (
                        <>
                          <UuViewer
                            onOpenHelp={() => {
                              setHelpDialogOpen({
                                fieldName:
                                  circumstanceSmartSuggestionsMap[
                                    potentialChangeInCircumstances
                                  ]?.fieldName,
                              });
                            }}
                            fieldName={
                              circumstanceSmartSuggestionsMap[
                                potentialChangeInCircumstances
                              ].fieldName
                            }
                            onDateChange={
                              circumstanceSmartSuggestionsMap[
                                potentialChangeInCircumstances
                              ].controller?.handleDateChange
                            }
                            initialMonth={
                              circumstanceSmartSuggestionsMap[
                                potentialChangeInCircumstances
                              ].controller?.initialMonth ?? ""
                            }
                            initialYear={
                              circumstanceSmartSuggestionsMap[
                                potentialChangeInCircumstances
                              ].controller?.initialYear ?? ""
                            }
                            dateDisabled={
                              circumstanceSmartSuggestionsMap[
                                potentialChangeInCircumstances
                              ].controller?.isSavingDate ?? false
                            }
                            dateError={Boolean(
                              circumstanceSmartSuggestionsMap[
                                potentialChangeInCircumstances
                              ].controller?.dateError,
                            )}
                            dateErrorText={
                              circumstanceSmartSuggestionsMap[
                                potentialChangeInCircumstances
                              ].controller?.dateError ?? ""
                            }
                            // showDatePicker={shouldShowDateSolution}
                            showUpload={Boolean(
                              circumstanceSmartSuggestionsMap[
                                potentialChangeInCircumstances
                              ].controller?.handleUploadDocument,
                            )}
                            uploadedDocument={
                              circumstanceSmartSuggestionsMap[
                                potentialChangeInCircumstances
                              ].controller?.uploadedDocument ?? null
                            }
                            onDocumentUpload={
                              circumstanceSmartSuggestionsMap[
                                potentialChangeInCircumstances
                              ].controller?.handleUploadDocument
                            }
                            onDocumentDelete={
                              circumstanceSmartSuggestionsMap[
                                potentialChangeInCircumstances
                              ].controller?.handleDeleteDocument
                            }
                            onDocumentView={
                              circumstanceSmartSuggestionsMap[
                                potentialChangeInCircumstances
                              ].controller?.handleViewDocument
                            }
                            documentUploading={
                              circumstanceSmartSuggestionsMap[
                                potentialChangeInCircumstances
                              ].controller?.isUploadingDocument ?? false
                            }
                          />
                        </>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              pt: 1.5,
              pb: 2,
              backgroundColor: "#E8EFEE",
              border: "1px solid #165F54",
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Stack sx={{ width: "100%" }}>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
                Pension Credit
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 400, mb: 1.5 }}>
                Is at least 1 person in the household in receipt of pension
                credit? 
                <Box component="span" sx={{ color: "red", ml: 0.5 }}>
                  *
                </Box>
              </Typography>
              <DoubleToggle
                value={recievesPensionCredit}
                onChange={setRecievesPensionCredit}
                selectedColor="#165F54"
                textColor="#FFFFFF"
                sx={{ mb: 2 }}
              />

              <Typography variant="h6" sx={{ fontWeight: 400, mb: 1.5 }}>
                Are all other adults in the property either in receipt of
                pension credit or state pension? 
                <Box component="span" sx={{ color: "red", ml: 0.5 }}>
                  *
                </Box>
              </Typography>
              <DoubleToggle
                value={recievesPensionCreditHousehold}
                onChange={setRecievesPensionCreditHousehold}
                selectedColor="#165F54"
                textColor="#FFFFFF"
                sx={{ mb: 2 }}
              />
            </Stack>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              pt: 1.5,
              pb: 2,
              backgroundColor: "#E8EFEE",
              border: "1px solid #165F54",
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Stack sx={{ width: "100%" }}>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 0 }}>
                Medical Condition
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 400, mb: 1.5 }}>
                Do you or anyone in the property have a medical condition that
                affects water usage?{" "}
                <Box component="span" sx={{ color: "red", ml: 0.5 }}>
                  *
                </Box>
              </Typography>
              <DoubleToggle
                value={hasMedicalCondition}
                onChange={setHasMedicalCondition}
                selectedColor="#165F54"
                textColor="#FFFFFF"
                sx={{ mb: 2 }}
              />

              {!UU_SHOW_HIDE_INFO.uuShowHideInfo && hasMedicalCondition ? (
                <UuViewer
                  onOpenHelp={() => {
                    setHelpDialogOpen({
                      fieldName: "medicalEvidence",
                    });
                  }}
                  headerTitle={"Smart Suggestion."}
                  fieldName={"medicalEvidence"}
                  {...(handleDateChangeWrapper && {
                    onDateChange: handleDateChangeWrapper,
                  })}
                  initialMonth={uuFieldState?.initialMonth ?? ""}
                  initialYear={uuFieldState?.initialYear ?? ""}
                  dateDisabled={uuFieldState?.isSavingDate ?? false}
                  dateError={Boolean(uuFieldState?.dateError)}
                  dateErrorText={uuFieldState?.dateError ?? ""}
                  showDatePicker={false}
                  showUpload={Boolean(uuFieldState?.handleUploadDocument)}
                  uploadedDocument={uuFieldState?.uploadedDocument ?? null}
                  {...(uuFieldState?.handleUploadDocument && {
                    onDocumentUpload: handleDocumentUpload,
                  })}
                  {...(uuFieldState?.handleDeleteDocument && {
                    onDocumentDelete: handleDeleteDocumentUU,
                  })}
                  {...(uuFieldState?.handleViewDocument && {
                    onDocumentView: uuFieldState.handleViewDocument,
                  })}
                  documentUploading={uuFieldState?.isUploadingDocument ?? false}
                  {...(uuFieldState?.handleDescriptionChange && {
                    onDescriptionChange: uuFieldState.handleDescriptionChange,
                  })}
                />
              ) : null}
            </Stack>
          </Box>

          <SmartSuggestionsListUU
            totalDocumentsLength={totalDocumentsLength}
            setSuggestionsDocumentsCount={setSuggestionsDocumentsCount}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              pt: 1.5,
              pb: 2,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Stack sx={{ width: "100%" }}>
              <Typography sx={{ fontWeight: 500, mb: 0, fontSize: "18px" }}>
                Add More Documents?
              </Typography>
              <List
                component="ul"
                sx={{
                  listStyleType: "disc",
                  pl: 3,
                  color: "#4B5563",
                }}
              >
                <ListItem component="li" sx={{ display: "list-item", p: 0 }}>
                  Please upload your supporting documents (you will need this
                  for a successful claim)
                </ListItem>

                <ListItem component="li" sx={{ display: "list-item", p: 0 }}>
                  <Link
                    underline="always"
                    sx={{
                      cursor: "pointer",
                      p: 0,
                      color: ThemeColours.labelVar2,
                    }}
                    component="button"
                    onClick={() => {
                      setOpenInfoDialog(true);
                    }}
                  >
                    How do I know what to upload?
                  </Link>{" "}
                </ListItem>

                <ListItem component="li" sx={{ display: "list-item", p: 0 }}>
                  If you are in receipt of Universal Credit and want an easy
                  guide on how to obtain your latest statement as a PDF document
                  please follow this link -
                  <Link
                    href="https://www.unitedutilities.com/providing-documentation/#:~:text=If%20you%20have%20been%20asked,them%20before%20sending%20to%20us."
                    sx={{ color: "#4B5563" }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ‎ Providing documentation | United Utilities
                  </Link>
                </ListItem>
              </List>

              <Stack spacing={2} sx={{ my: 1 }}>
                <DocumentsList
                  notesField="description"
                  showCheckbox={true}
                  company={company}
                  documents={documents.map((doc) => ({
                    ...doc,
                    toggleNotes: (id, value) => {
                      setDocuments((prev) =>
                        prev.map((d) =>
                          d.id === String(id) ? { ...d, showNotes: value } : d,
                        ),
                      );
                    },
                  }))}
                  onOpen={handleOpenPreview as (doc: any) => void}
                  onDelete={(id: string | number) =>
                    handleDeleteDocument(String(id))
                  }
                  onNameChange={(doc, newDescription) =>
                    handleDocumentInput(String(doc.id), newDescription)
                  }
                  onCheck={(id: string | number) =>
                    handleCheckDocument(String(id))
                  }
                />
              </Stack>

              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontWeight: 500,
                  py: 1,
                  mt: 1,
                  boxShadow: "none",
                  maxWidth: 200,
                  backgroundColor: "#165F54",
                  fontSize: 16,
                }}
                onClick={handleFileSelect}
              >
                Add Document
              </Button>
            </Stack>
          </Box>

          {/* File Upload Section */}
          {/* <Box sx={{ mb: 2 }}>
            <Box
              onClick={handleFileSelect}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                border: isDragging ? "2px solid #0d9488" : "2px dashed #000",
                borderRadius: 1,
                p: 2,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "#DCFCE7",
                  width: 64,
                  height: 64,
                  mb: 1,
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 30, color: "#165F54" }} />
              </Avatar>

              <Typography sx={{ fontWeight: 500, fontSize: 18, mb: 0 }}>
                Drag & drop files here
              </Typography>
              <Typography
                sx={{ fontWeight: 400, fontSize: 13, color: "#4B5563", mb: 1 }}
              >
                or tap to browse your device
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontWeight: 400,
                  mb: 1,
                  py: 1.3,
                  px: 3.5,
                  boxShadow: "none",
                  backgroundColor: "#E8EFEE",
                  color: "#6C7A7B",
                  "&:hover": {
                    backgroundColor: "#ccd4d3ff",
                    boxShadow: "none",
                  },
                }}
                endIcon={
                  isUploading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : undefined
                }
              >
                Share Securely
              </Button>
              <Typography variant="caption" sx={{ color: "#9CA3AE" }}>
                PDF, JPG, PNG up to 5 MB
              </Typography>
            </Box>

            <DocumentPreviewDialog
              open={openPreview}
              onClose={handleClosePreview}
              documentName={previewFileName ?? "Document Preview"}
              documentUrl={previewBlobUrl}
              isLoading={loadingPreview}
              error={previewError}
              mimeType={previewFileType ?? undefined}
            />

            <input
              id="file-upload-input"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
            />
          </Box> */}

          {totalDocumentsLength >= 10 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                pt: 1.5,
                pb: 2,
                backgroundColor: "#fff",
                border: "1px solid #ddd",
                borderRadius: 1,
                mb: 3,
              }}
            >
              <Stack sx={{ width: "100%" }}>
                <Typography sx={{ fontWeight: 400, mb: 0, fontSize: "18px" }}>
                  Upload documents
                </Typography>
                <List
                  component="ul"
                  sx={{
                    listStyleType: "disc",
                    pl: 3,
                    color: "#4B5563",
                  }}
                >
                  <ListItem component="li" sx={{ display: "list-item", p: 0 }}>
                    You have reached the limit of 10 documents. To continue,
                    please remove some of your existing documents.
                  </ListItem>
                </List>
              </Stack>
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 500, mb: 1, color: "#1E40AF" }}
            >
              1. United Utilities Declaration
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 400, mb: 1, color: "#1E40AF" }}
            >
              The information I have provided is correct to the best of my
              knowledge and I have submitted accurate household income
              information. I understand that if I provide any information which
              is false, this may result in me being removed from any schemes I
              am accepted for based on information provided in this application.
              If there is a change in my household’s circumstances that may
              affect my application, I will tell you straight away. I understand
              that United Utilities may contact the authority that provides my
              benefits to verify the information I have provided, and that
              United Utilities may also verify the declared income information
              with a Credit Reference Agency. I confirm I will abide by the
              terms and conditions of the scheme.
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 500, mb: 1, color: "#1E40AF" }}
            >
              2. WaterSure Declaration
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 400, mb: 1, color: "#1E40AF" }}
            >
              If I have made an application because of a medical condition, if
              requested I agree to provide evidence of the eligible medical
              condition to United Utilities.  I understand further information
              may be required to support the processing of my application
              relating to the medical condition and need to use more water.
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 500, mb: 1, color: "#1E40AF" }}
            >
              3. Water usage Declaration
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 400, mb: 1, color: "#1E40AF" }}
            >
              In applying for an affordability tariff, I can confirm that the
              water supplied to the premises is not used for:
              <List
                component="ul"
                sx={{
                  listStyleType: "disc",
                  pl: 4,
                  color: "#1E40AF",
                }}
              >
                <ListItem component="li" sx={{ display: "list-item", p: 0 }}>
                  Watering a garden (other than by hand) by means of any
                  apparatus OR
                </ListItem>
                <ListItem component="li" sx={{ display: "list-item", p: 0 }}>
                  Automatically replenishing a pond or swimming pool with a
                  capacity of greater than 10,000 litres
                </ListItem>
              </List>
            </Typography>
          </Box>

          <Box id="radio-cards">
            {radioCards.map((card) => (
              <SelectableCard
                key={card.title}
                title={card.title}
                description={card.description}
                selected={status}
                onSelect={setStatus}
              />
            ))}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 4,
            py: 2,
            borderTop: "1px solid #e0e0e0",
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            zIndex: 1,
            boxShadow: "0 -4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{ textTransform: "none" }}
            size="large"
          >
            Save
          </Button>
        </DialogActions>
      </Box>

      {/* JSON Help Dialog */}
      {helpDialogOpen &&
        (() => {
          const fieldName = helpDialogOpen.fieldName;
          if (!fieldName) return null;

          const helpContent = (suggestionsHelpContent as Record<string, any>)[
            fieldName
          ];
          if (!helpContent) return null;

          console.log(helpContent);

          return (
            <JsonHelpDialog
              open={true}
              onClose={() => setHelpDialogOpen(null)}
              title={helpContent.title || "Help & support"}
              description={helpContent.description}
              faqs={helpContent.faqs || []}
              zIndex={1302}
            />
          );
        })()}

      {/* UU Document Preview Dialog */}
      <DocumentPreviewDialog
        open={uuController.previewDialog.isOpen}
        onClose={uuController.closePreviewDialog}
        documentName={uuController.previewDialog.documentName}
        documentUrl={uuController.previewDialog.documentUrl}
        isLoading={uuController.previewDialog.isLoading}
        error={uuController.previewDialog.error}
        mimeType={uuController.previewDialog.mimeType}
      />
    </>
  );
};

export default UUDeclarationDialog;
